import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { Check, NavigateNext, NavigateBefore } from '@mui/icons-material';

const steps = [
  'Informations personnelles',
  'Informations professionnelles',
  'Validation',
];

const QuestionnaireStep = ({ step, formData, setFormData, errors }) => {
  const theme = useTheme();

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Nom"
                value={formData.lastName || ''}
                onChange={handleChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Prénom"
                value={formData.firstName || ''}
                onChange={handleChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                variant="outlined"
              />
            </Box>
          </motion.div>
        );
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Type d'entreprise</FormLabel>
                <RadioGroup
                  value={formData.companyType || ''}
                  onChange={handleChange('companyType')}
                >
                  <FormControlLabel
                    value="sasu"
                    control={<Radio />}
                    label="SASU"
                  />
                  <FormControlLabel value="eurl" control={<Radio />} label="EURL" />
                  <FormControlLabel value="sarl" control={<Radio />} label="SARL" />
                </RadioGroup>
              </FormControl>
              <TextField
                label="Nom de l'entreprise"
                value={formData.companyName || ''}
                onChange={handleChange('companyName')}
                error={!!errors.companyName}
                helperText={errors.companyName}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Numéro SIRET"
                value={formData.siret || ''}
                onChange={handleChange('siret')}
                error={!!errors.siret}
                helperText={errors.siret}
                fullWidth
                variant="outlined"
              />
            </Box>
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              >
                <Check
                  sx={{
                    fontSize: 80,
                    color: theme.palette.success.main,
                    mb: 2,
                  }}
                />
              </motion.div>
              <Typography variant="h5" gutterBottom>
                Récapitulatif
              </Typography>
              <Box sx={{ mt: 3, textAlign: 'left' }}>
                {Object.entries(formData).map(([key, value]) => (
                  <Typography key={key} variant="body1" sx={{ mb: 1 }}>
                    <strong>{key}:</strong> {value}
                  </Typography>
                ))}
              </Box>
            </Box>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return renderStep();
};

const Questionnaire = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateStep = () => {
    const newErrors = {};
    switch (activeStep) {
      case 0:
        if (!formData.lastName) newErrors.lastName = 'Le nom est requis';
        if (!formData.firstName) newErrors.firstName = 'Le prénom est requis';
        if (!formData.email) newErrors.email = "L'email est requis";
        else if (!/\S+@\S+\.\S+/.test(formData.email))
          newErrors.email = 'Email invalide';
        break;
      case 1:
        if (!formData.companyType)
          newErrors.companyType = "Le type d'entreprise est requis";
        if (!formData.companyName)
          newErrors.companyName = "Le nom de l'entreprise est requis";
        if (!formData.siret) newErrors.siret = 'Le numéro SIRET est requis';
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Simuler un appel API
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    // Gérer la soumission ici
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.dark} 100%)`,
        p: 4,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h3"
          sx={{
            mb: 4,
            color: 'white',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          Questionnaire
        </Typography>
      </motion.div>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 800,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          },
        }}
      >
        <Stepper
          activeStep={activeStep}
          sx={{ mb: 4 }}
          alternativeLabel
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 300, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, mb: 4 }}>
            <AnimatePresence mode="wait">
              <QuestionnaireStep
                step={activeStep}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
              />
            </AnimatePresence>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<NavigateBefore />}
              sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
            >
              Précédent
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  <Check />
                ) : (
                  <NavigateNext />
                )
              }
              disabled={isSubmitting}
            >
              {activeStep === steps.length - 1 ? 'Soumettre' : 'Suivant'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Questionnaire; 